import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  getGetInterviewInviteQueryOptions,
  getListInterviewInvitesQueryKey,
  useDeleteInterviewInvite,
} from '@sara/api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@sara/ui/components/alert-dialog';
import { buttonVariants } from '@sara/ui/components/button';
import { toast } from '@sara/ui/components/sonner';
import { cn } from '@sara/ui/lib/utils';
import { interviewsHomeSearch } from '../../../../router/tree';
import { useOrgSlug } from '../../../auth';
import { InterviewBriefDetail } from '../../components/InterviewBriefDetail';
import { toSuspense } from '../../../../lib/query';
import type { InterviewBriefDetailContainerProps } from './types';

export function InterviewBriefDetailContainerContent({
  inviteId,
  renderAttempts,
}: InterviewBriefDetailContainerProps) {
  const orgSlug = useOrgSlug();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { data: invite } = useSuspenseQuery(
    toSuspense(getGetInterviewInviteQueryOptions(inviteId))
  );

  const { mutate: deleteInvite, isPending: isDeleting } = useDeleteInterviewInvite({
    mutation: {
      onSuccess: () => {
        toast.success('Session deleted');
        void queryClient.invalidateQueries({ queryKey: getListInterviewInvitesQueryKey() });
        void navigate({
          to: '/orgs/$orgSlug/sessions',
          params: { orgSlug },
          search: interviewsHomeSearch,
        });
      },
      onError: () => toast.error('Could not delete the session. Please try again.'),
    },
  });

  // The header + external identifiers always render; InterviewBriefDetail shows
  // the "not started" placeholder in place of results when there's no session.
  return (
    <>
      <InterviewBriefDetail
        invite={invite}
        renderAttempts={() => renderAttempts(invite.id)}
        onDelete={() => setConfirmingDelete(true)}
      />

      <AlertDialog
        open={confirmingDelete}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setConfirmingDelete(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes{' '}
              <span className="font-medium text-foreground">
                {invite.candidate.firstName} {invite.candidate.lastName}
              </span>
              &apos;s session — including all call attempts, transcripts, and results — and the
              candidate&apos;s interview link will stop working. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(buttonVariants({ variant: 'outline', shape: 'pill', size: 'sm' }))}
              disabled={isDeleting}
              data-testid="delete-session-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive', shape: 'pill', size: 'sm' }))}
              disabled={isDeleting}
              data-testid="delete-session-confirm"
              onClick={(event) => {
                event.preventDefault();
                deleteInvite({ id: invite.id });
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete session'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
